import { User } from 'firebase/auth';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

export interface AuthGuardOptions {
  redirectTo?: string;
  requireAuth?: boolean;
}

export class AuthGuardService {
  private static instance: AuthGuardService;
  private currentUser: User | null = null;
  private authStateListeners: ((user: User | null) => void)[] = [];

  private constructor() {
    // Écouter les changements d'état d'authentification
    onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
      this.authStateListeners.forEach(listener => listener(user));
    });
  }

  public static getInstance(): AuthGuardService {
    if (!AuthGuardService.instance) {
      AuthGuardService.instance = new AuthGuardService();
    }
    return AuthGuardService.instance;
  }

  /**
   * Vérifie si l'utilisateur est connecté
   */
  public isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  /**
   * Obtient l'utilisateur actuel
   */
  public getCurrentUser(): User | null {
    return this.currentUser;
  }

  /**
   * Ajoute un listener pour les changements d'état d'authentification
   */
  public onAuthStateChange(callback: (user: User | null) => void): () => void {
    this.authStateListeners.push(callback);
    
    // Retourne une fonction pour supprimer le listener
    return () => {
      const index = this.authStateListeners.indexOf(callback);
      if (index > -1) {
        this.authStateListeners.splice(index, 1);
      }
    };
  }

  /**
   * Redirige vers la page de connexion avec l'URL de retour
   */
  public redirectToLogin(returnUrl?: string): void {
    const currentPath = returnUrl || window.location.pathname + window.location.search;
    const loginUrl = `/login?returnUrl=${encodeURIComponent(currentPath)}`;
    window.location.href = loginUrl;
  }

  /**
   * Redirige vers la page d'inscription avec l'URL de retour
   */
  public redirectToRegister(returnUrl?: string): void {
    const currentPath = returnUrl || window.location.pathname + window.location.search;
    const registerUrl = `/register?returnUrl=${encodeURIComponent(currentPath)}`;
    window.location.href = registerUrl;
  }

  /**
   * Vérifie l'authentification et redirige si nécessaire
   */
  public checkAuthAndRedirect(options: AuthGuardOptions = {}): boolean {
    const { requireAuth = true, redirectTo } = options;

    if (requireAuth && !this.isAuthenticated()) {
      if (redirectTo) {
        this.redirectToLogin(redirectTo);
      } else {
        this.redirectToLogin();
      }
      return false;
    }

    return true;
  }

  /**
   * Gère l'action d'essai gratuit avec vérification d'authentification
   */
  public handleTrialAction(planType: 'professional', onSuccess?: () => void): void {
    if (!this.isAuthenticated()) {
      // Stocker l'intention d'essai dans le localStorage
      localStorage.setItem('pendingTrialAction', JSON.stringify({
        planType,
        action: 'trial',
        timestamp: Date.now()
      }));
      
      this.redirectToRegister(`/trial/${planType}`);
      return;
    }

    // Utilisateur connecté, procéder à l'essai
    if (onSuccess) {
      onSuccess();
    }
  }

  /**
   * Gère l'action d'abonnement avec vérification d'authentification
   */
  public handleSubscriptionAction(planType: 'professional', onSuccess?: () => void): void {
    if (!this.isAuthenticated()) {
      // Stocker l'intention d'abonnement dans le localStorage
      localStorage.setItem('pendingSubscriptionAction', JSON.stringify({
        planType,
        action: 'subscription',
        timestamp: Date.now()
      }));
      
      this.redirectToRegister(`/subscribe/${planType}`);
      return;
    }

    // Utilisateur connecté, procéder à l'abonnement
    if (onSuccess) {
      onSuccess();
    }
  }

  /**
   * Récupère et traite les actions en attente après connexion
   */
  public processPendingActions(): { action: string; planType: string } | null {
    const pendingTrial = localStorage.getItem('pendingTrialAction');
    const pendingSubscription = localStorage.getItem('pendingSubscriptionAction');

    if (pendingTrial) {
      try {
        const trialData = JSON.parse(pendingTrial);
        // Vérifier que l'action n'est pas trop ancienne (24h max)
        if (Date.now() - trialData.timestamp < 24 * 60 * 60 * 1000) {
          localStorage.removeItem('pendingTrialAction');
          return { action: 'trial', planType: trialData.planType };
        }
        localStorage.removeItem('pendingTrialAction');
      } catch (error) {
        console.error('Erreur lors du traitement de l\'action d\'essai en attente:', error);
        localStorage.removeItem('pendingTrialAction');
      }
    }

    if (pendingSubscription) {
      try {
        const subscriptionData = JSON.parse(pendingSubscription);
        // Vérifier que l'action n'est pas trop ancienne (24h max)
        if (Date.now() - subscriptionData.timestamp < 24 * 60 * 60 * 1000) {
          localStorage.removeItem('pendingSubscriptionAction');
          return { action: 'subscription', planType: subscriptionData.planType };
        }
        localStorage.removeItem('pendingSubscriptionAction');
      } catch (error) {
        console.error('Erreur lors du traitement de l\'action d\'abonnement en attente:', error);
        localStorage.removeItem('pendingSubscriptionAction');
      }
    }

    return null;
  }

  /**
   * Nettoie les actions en attente
   */
  public clearPendingActions(): void {
    localStorage.removeItem('pendingTrialAction');
    localStorage.removeItem('pendingSubscriptionAction');
  }
}

// Export de l'instance singleton
export const authGuard = AuthGuardService.getInstance();